import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { LuMountain, LuWaves, LuCoffee, LuMedal, LuTrees, LuUtensils, LuSunrise, LuCompass, LuBird, LuHeart } from 'react-icons/lu';
import { PageWrapper } from '../components/PageWrapper';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } })
};

function AnimSection({ children, className, style }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className} style={style}>
      {children}
    </motion.div>
  );
}

const highlights = [
  { icon: <LuMountain />, label: 'Chembra Peak View', desc: '360° panoramic vista from your doorstep' },
  { icon: <LuWaves />, label: 'Soojipara Falls', desc: '3-step waterfall just 1.5km away' },
  { icon: <LuCoffee />, label: 'Tea Plantation', desc: 'Surrounded by Harrison tea estates' },
  { icon: <LuMedal />, label: 'Diamond Class Certified', desc: 'Since 2019 · Dept. of Tourism, Govt. of Kerala' },
];

const amenityPreviews = [
  { icon: <LuTrees />, title: 'Nature Trails', desc: 'Guided forest walks & river trekking' },
  { icon: <LuUtensils />, title: 'Kerala Cuisine', desc: 'Authentic home-cooked meals with love' },
  { icon: <LuSunrise />, title: 'Sunrise Views', desc: 'Wake up to misty Wayanad mornings' },
  { icon: <LuCompass />, title: 'Adventure Sports', desc: 'Zipline, trekking & waterfall visits' },
  { icon: <LuBird />, title: 'Bird Watching', desc: 'Rich biodiversity in the Western Ghats' },
  { icon: <LuHeart />, title: 'Complete Serenity', desc: 'Disconnect to truly reconnect' },
];

export default function Home() {
  const { data: featuredRooms } = useQuery({
    queryKey: ['featured-rooms'],
    queryFn: () => axios.get('/api/rooms?featured=true').then(r => r.data)
  });
  const { data: reviewData } = useQuery({
    queryKey: ['featured-reviews'],
    queryFn: () => axios.get('/api/reviews?featured=true&limit=3').then(r => r.data)
  });

  const [heroLoaded, setHeroLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setHeroLoaded(true), 100); }, []);

  return (
    <PageWrapper>
      <Helmet>
        <title>Villa Tesori | Best Diamond Class Homestay in Wayanad, Kerala</title>
        <meta name="description" content="Stay at Villa Tesori, the best Diamond Class certified homestay in Vellarimala, Wayanad. 360° views of Chembra Peak, luxury rooms, authentic Kerala food, and trekking near Soojipara Falls." />
        <meta name="keywords" content="Villa Tesori, best homestay in Wayanad, luxury homestay Kerala, Vellarimala stays, Chembra Peak view rooms, Soojipara Falls accommodation, family homestay Wayanad, Diamond Class Kerala tourism" />
        <link rel="icon" type="image/png" href="/favicon-final.png?v=5" />
        <link rel="apple-touch-icon" href="/favicon-final.png?v=5" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://villatesori.com/" />
        <meta property="og:title" content="Villa Tesori – Luxury Stay in Wayanad with Chembra Peak Views" />
        <meta property="og:description" content="Experience premium hospitality at Villa Tesori. Diamond Class Certified. Nestled in tea plantations with 360° mountain views." />
        <meta property="og:image" content="https://villatesori.com/logo-green.jpeg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Villa Tesori – Luxury Homestay in Wayanad" />
        <meta name="twitter:description" content="Wayanad's finest Diamond Class Homestay with panoramic views and waterfall treks." />
        <meta name="twitter:image" content="https://villatesori.com/logo-green.jpeg" />

        <link rel="canonical" href="https://villatesori.com/" />

        {/* Schema Markup for Google */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BedAndBreakfast",
              "name": "Villa Tesori | Best Diamond Class Homestay in Wayanad",
              "image": "https://villatesori.com/logo-green.jpeg",
              "logo": "https://villatesori.com/logo-green.jpeg",
              "@id": "https://villatesori.com",
              "url": "https://villatesori.com",
              "telephone": "+917558806677",
              "priceRange": "INR",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Near Soojipara Waterfalls, Elavayal, Puthumala, Meppady",
                "addressLocality": "Wayanad",
                "addressRegion": "Kerala",
                "postalCode": "673577",
                "addressCountry": "IN"
              },
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": 11.4862,
                "longitude": 76.1950
              },
              "starRating": {
                "@type": "Rating",
                "ratingValue": "5"
              },
              "amenityFeature": [
                "Air Conditioning",
                "Free WiFi",
                "Mountain View",
                "Private Balcony",
                "Authentic Kerala Meals",
                "Near Soojipara Waterfalls"
              ],
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "5.0",
                "reviewCount": "500"
              }
            }
          `}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="home-hero" style={{ position: 'relative', height: '90vh', minHeight: 500, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(/img1.jpeg)`,
          backgroundSize: 'cover', backgroundPosition: 'top center',
          transform: heroLoaded ? 'scale(1)' : 'scale(1.03)',
          transition: 'transform 2.5s cubic-bezier(0.2, 0, 0.2, 1)',
          filter: 'brightness(0.8) contrast(1.05)',
        }} />

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(26,46,26,0.7) 0%, transparent 60%)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 240, background: 'linear-gradient(to top, rgba(26,46,26,0.5), transparent)' }} />

        {/* Side links — hidden on mobile via CSS */}
        <div className="hero-side-links" style={{ position: 'absolute', top: '50%', right: 60, transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {['Gallery', 'Rooms', 'About'].map((label) => (
            <Link key={label} to={`/${label.toLowerCase()}`} style={{
              writingMode: 'vertical-rl', letterSpacing: '0.2em', fontSize: '0.68rem',
              textTransform: 'uppercase', color: 'rgba(226,201,126,0.6)',
              fontFamily: 'Jost, sans-serif', transition: 'color 0.3s',
            }}
              onMouseEnter={e => e.target.style.color = 'var(--gold-light)'}
              onMouseLeave={e => e.target.style.color = 'rgba(226,201,126,0.6)'}
            >{label}</Link>
          ))}
          <div style={{ width: 1, height: 60, background: 'rgba(201,168,76,0.35)' }} />
        </div>

        <div style={{ 
          position: 'relative', 
          zIndex: 1, 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          paddingLeft: 'clamp(24px, 8vw, 80px)', 
          paddingRight: 24 
        }}>
          <div style={{ maxWidth: 800, width: '100%' }}>
            {/* Elegant Status Label */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}
            >
              <div style={{ width: 40, height: 1, background: 'var(--gold)' }} />
              <span style={{ 
                fontFamily: 'Jost, sans-serif', 
                fontSize: '0.72rem', 
                letterSpacing: '0.35em', 
                textTransform: 'uppercase', 
                color: 'var(--gold-light)',
                fontWeight: 400
              }}>
                Diamond Class Homestay
              </span>
            </motion.div>

            {/* Main Brand Heading */}
            <div style={{ marginBottom: 40 }}>
              <motion.h1
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
                style={{ 
                  fontFamily: 'Cormorant Garamond, serif', 
                  fontSize: 'clamp(2.2rem, 8vw, 5rem)', 
                  fontWeight: 300, 
                  color: 'var(--white)', 
                  lineHeight: 1.1,
                  marginBottom: 10,
                  textShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                Villa Tesori
              </motion.h1>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}
                style={{ 
                  fontFamily: 'Pinyon Script, cursive', 
                  fontSize: 'clamp(1.8rem, 4.5vw, 3.2rem)', 
                  color: 'var(--gold-light)',
                  marginLeft: 'clamp(2px, 2vw, 20px)'
                }}
              >
                Serenity at its Peak
              </motion.div>
            </div>

            {/* Refined Description */}
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }}
              style={{ 
                fontFamily: 'Cormorant Garamond, serif', 
                fontSize: 'clamp(1.1rem, 2.2vw, 1.4rem)', 
                color: 'rgba(245,240,232,0.9)', 
                lineHeight: 1.8, 
                maxWidth: 540, 
                marginBottom: 48, 
                fontStyle: 'italic',
                fontWeight: 300
              }}
            >
              A hidden treasure nestled in the misty tea gardens of Wayanad, where the luxury of nature meets timeless hospitality.
            </motion.p>

            {/* High-End Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
              style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" 
                className="btn btn-gold" 
                style={{ 
                  padding: '12px 32px', 
                  fontSize: '0.75rem',
                  textDecoration: 'none'
                }}
              >
                Reserve Your Stay
              </a>
              <Link to="/rooms" 
                style={{ 
                  fontFamily: 'Jost, sans-serif', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.2em', 
                  textTransform: 'uppercase', 
                  color: 'var(--white)',
                  textDecoration: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                  paddingBottom: 4,
                  transition: 'all 0.3s'
                }}
                onMouseEnter={e => e.target.style.borderColor = 'var(--gold)'}
                onMouseLeave={e => e.target.style.borderColor = 'rgba(255,255,255,0.3)'}
              >
                Explore Rooms →
              </Link>
            </motion.div>
          </div>
        </div>

      </section>

      {/* HIGHLIGHTS BAR */}
      <section style={{ background: 'var(--forest)', padding: '40px 0', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
        <div className="container">
          <div className="highlights-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32 }}>
            {highlights.map((h, i) => (
              <AnimSection key={h.label} custom={i}>
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <span style={{ fontSize: '1.8rem', marginBottom: 10, display: 'block', color: 'var(--gold)' }}>{h.icon}</span>
                  <h4 style={{ color: 'var(--gold-light)', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem', marginBottom: 4 }}>{h.label}</h4>
                  <p style={{ color: 'rgba(200,217,192,0.7)', fontSize: '0.82rem', lineHeight: 1.5 }}>{h.desc}</p>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* REDESIGNED OUR STORY SECTION - ARTISTIC COLLAGE VERSION */}
      <section className="section" style={{ background: 'var(--cream)', paddingBottom: 140 }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="section-label" style={{ justifyContent: 'center' }}>The Essence</div>
            <h2 className="section-title" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>
              Where Luxury Meets <span className="script text-gold">the Wild</span>
            </h2>
          </div>

          <div className="collage-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <motion.div
              initial={{ x: -40, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="collage-item"
              style={{ height: 500, borderRadius: 4, overflow: 'hidden', boxShadow: 'var(--shadow-deep)' }}
            >
              <img src="/img2.jpeg" alt="Villa Exterior" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </motion.div>

            <motion.div
              initial={{ x: 40, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="about-text-card"
              style={{ padding: '40px', background: 'var(--white)', boxShadow: 'var(--shadow-deep)', borderLeft: '5px solid var(--gold)', borderRadius: 2 }}
            >
              <h3 style={{ fontSize: '1.8rem', color: 'var(--forest)', marginBottom: 16 }}>A Legacy of Hospitality</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: 24 }}>
                Experience the authentic charm of Wayanad at Villa Tesori. Hosted by Nimmy and Dr. Raphi, our Diamond Class sanctuary offers a curated blend of wilderness and comfort. Our story is woven into the tea-clad hills, offering a personal touch that transforms a stay into a homecoming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
                <Link to="/about" className="btn btn-primary" style={{ padding: '10px 24px', fontSize: '0.7rem' }}>Our Story</Link>
                <div style={{ fontFamily: 'Pinyon Script, cursive', fontSize: '1.6rem', color: 'var(--gold)' }}>Nimmy & Raphi</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ROOMS PREVIEW */}
      {featuredRooms?.length > 0 && (
        <section className="section" style={{ background: 'var(--cream)' }}>
          <div className="container">
            <AnimSection style={{ textAlign: 'center', marginBottom: 60 }}>
              <div className="section-label" style={{ justifyContent: 'center' }}>Accommodations</div>
              <h2 className="section-title">Rooms & Suites</h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>Each room is a sanctuary — crafted for comfort with views that humble the spirit.</p>
            </AnimSection>
            <div className="grid-3">
              {featuredRooms.slice(0, 3).map((room, i) => (
                <AnimSection key={room._id} custom={i}>
                  <Link to={`/rooms/${room.slug}`} className="card">
                    <div className="img-overlay" style={{ height: 260 }}>
                      <img src={room.images?.[0]?.url || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600'} alt={room.name} />
                      <div style={{ position: 'absolute', top: 16, left: 16 }}>
                        <span className="badge badge-gold">{room.type}</span>
                      </div>
                    </div>
                    <div style={{ padding: '24px 28px' }}>
                      <h3 style={{ fontSize: '1.4rem', marginBottom: 8 }}>{room.name}</h3>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 16, lineHeight: 1.7 }}>{room.shortDesc}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.6rem', color: 'var(--forest)' }}>₹{room.pricePerNight?.toLocaleString()}</span>
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: 4 }}>/night</span>
                        </div>
                        <span style={{ fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)', fontFamily: 'Jost, sans-serif' }}>View →</span>
                      </div>
                    </div>
                  </Link>
                </AnimSection>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <Link to="/rooms" className="btn btn-outline">View All Rooms</Link>
            </div>
          </div>
        </section>
      )}

      {/* AMENITIES */}
      <section className="section" style={{ background: 'var(--forest)' }}>
        <div className="container">
          <AnimSection style={{ textAlign: 'center', marginBottom: 60 }}>
            <div className="section-label" style={{ justifyContent: 'center', color: 'var(--gold)' }}>Experiences</div>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 4vw, 3.4rem)', color: 'var(--mist)' }}>
              Every Moment, <em style={{ color: 'var(--gold-light)' }}>Unforgettable</em>
            </h2>
          </AnimSection>
          <div className="grid-3">
            {amenityPreviews.map((a, i) => (
              <AnimSection key={a.title} custom={i}>
                <div style={{
                  padding: '36px 32px', border: '1px solid rgba(201,168,76,0.18)',
                  borderRadius: 2, transition: 'all 0.4s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.5)'; e.currentTarget.style.background = 'rgba(201,168,76,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.18)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: '2rem', marginBottom: 16, display: 'block', color: 'var(--gold)' }}>{a.icon}</span>
                  <h3 style={{ color: 'var(--mist)', fontSize: '1.2rem', marginBottom: 10 }}>{a.title}</h3>
                  <p style={{ color: 'rgba(200,217,192,0.65)', fontSize: '0.88rem', lineHeight: 1.7 }}>{a.desc}</p>
                </div>
              </AnimSection>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Link to="/amenities" className="btn btn-gold">Explore Experiences</Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {reviewData?.reviews?.length > 0 && (
        <section className="section" style={{ background: 'var(--parchment)' }}>
          <div className="container">
            <AnimSection style={{ textAlign: 'center', marginBottom: 60 }}>
              <div className="section-label" style={{ justifyContent: 'center' }}>Guest Stories</div>
              <h2 className="section-title">What Our Guests Say</h2>
            </AnimSection>
            <div className="grid-3">
              {reviewData.reviews.map((review, i) => (
                <AnimSection key={review._id} custom={i}>
                  <div style={{ background: 'var(--white)', padding: '36px 32px', borderRadius: 2, height: '100%' }}>
                    <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
                      {[1, 2, 3, 4, 5].map(s => (
                        <span key={s} style={{ color: s <= review.rating ? 'var(--gold)' : 'var(--mist)', fontSize: '0.9rem' }}>★</span>
                      ))}
                    </div>
                    <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem', fontStyle: 'italic', lineHeight: 1.8, color: 'var(--text-body)', marginBottom: 20 }}>
                      "{review.content.slice(0, 180)}{review.content.length > 180 ? '...' : ''}"
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid var(--mist)', paddingTop: 16 }}>
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--forest)', color: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Jost, sans-serif', fontWeight: 500, fontSize: '0.85rem', flexShrink: 0 }}>
                        {review.guestName.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.9rem', color: 'var(--forest)' }}>{review.guestName}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{review.location}</div>
                      </div>
                    </div>
                  </div>
                </AnimSection>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <Link to="/reviews" className="btn btn-outline">Read All Reviews</Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA BANNER */}
      <section style={{
        position: 'relative', padding: 'clamp(60px,10vw,120px) 0', overflow: 'hidden',
        backgroundImage: 'url(https://images.unsplash.com/photo-1433086966358-54859d0ed716?w=1800)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,46,26,0.75)' }} />
        <div className="container" style={{ position: 'relative', textAlign: 'center' }}>
          <AnimSection>
            <span style={{ fontFamily: 'Pinyon Script, cursive', fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', color: 'var(--gold-light)', display: 'block', marginBottom: 16 }}>
              Begin Your Journey
            </span>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 5vw, 4rem)', color: 'var(--white)', marginBottom: 20, fontWeight: 300 }}>
              Your Perfect Escape Awaits
            </h2>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', color: 'rgba(245,240,232,0.8)', fontStyle: 'italic', maxWidth: 560, margin: '0 auto 40px', lineHeight: 1.8 }}>
              13km from Meppadi, a world away from ordinary. Come find your treasure in the hills of Wayanad.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://wa.me/917558806677" target="_blank" rel="noopener noreferrer" className="btn btn-gold" style={{ fontSize: '0.78rem', textDecoration: 'none' }}>Book Now</a>
              <Link to="/gallery" className="btn" style={{ background: 'transparent', border: '1px solid rgba(245,240,232,0.5)', color: 'var(--white)', fontSize: '0.78rem' }}>View Gallery</Link>
            </div>
          </AnimSection>
        </div>
      </section>
    </PageWrapper>
  );
}
